import { ReactNode } from 'react';
import { v4 as uuid } from 'uuid';
import { create } from 'zustand';
import { callFunction } from '../lib/Helpers';
import { TeamsUserCredential } from '@microsoft/teamsfx';
import { toast } from 'react-toastify';

export interface Block {
  id: string;
  html: string;
  controls: BlockControl;
  selections: EffectSelection[];
}
export interface EffectSelection {
  id: string;
  start: number;
  end: number;
  controls: {
    control: string;
    value: string | PronunciationEffect;
  }[];
}
export interface Speaker {
  key: string;
  text: string;
  name: string;
  gender: string;
  locale: string;
  samplerate: string;
  type: string;
  status: string;
  styles: string[];
  sampleaudio: string;
}
export interface PronunciationEffect {
  type: 'interpret' | 'alias';
  value: string;
}
export interface BreakEffect {
  type: 'time' | 'strength';
  value: string;
}
export interface BlockControl {
  speaker: Speaker;
  speed: string;
  pitch: string;
  volume: string;
  style: string | undefined;
  pronunciation: PronunciationEffect | null;
  break: BreakEffect | null;
}
export interface CachedAudio {
  blockId: string;
  url: string;
  hasChanged: boolean;
}

interface BlockElement {
  id: string;
  ref: HTMLElement | null;
  index: number;
}

export interface ProjectInfo {
  id: string;
  name: string;
  description: string;
}

interface PageState {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  blocks: Block[];

  getBlock: (id: string) => Block;
  updateBlockContent: (id: string, content: string) => void;
  updateBlockControl: (
    id: string,
    controlName: keyof BlockControl,
    controlValue: any
  ) => void;
  clearBlockControls: (id: string) => void;
  addBlock: (currentBlock: BlockElement, newBlockContent: string) => void;
  deleteBlock: (currentBlock: BlockElement) => void;
  convertToBlocks: (
    content: string,
    id: string,
    cursorPosition: number,
    rangeCount: number
  ) => void;

  createEffectSelection: (
    blockId: string,
    start: number,
    end: number,
    control: string,
    value: string | PronunciationEffect
  ) => void;
  removeEffectFromSelection: (
    blockId: string,
    selectionId: string,
    effectName: string
  ) => void;
  deleteEffectSelection: (blockId: string, selections: string[]) => void;

  cachedAudios: CachedAudio[];
  setCachedAudios(cachedAudios: CachedAudio[]): void;

  hasAddedBlock: BlockElement | undefined;
  setHasAddedBlock: (blockElement: BlockElement | undefined) => void;

  projectInfo: ProjectInfo | undefined;
  setProjectInfo: (projectInfo: ProjectInfo | undefined) => void;

  createProject: () => void;
  loadProject: (
    id: string,
    credentials: TeamsUserCredential | undefined
  ) => void;
}

export const INITIAL_CONTROLS: BlockControl = {
  speaker: {
    gender: 'Female',
    key: 'Jenny',
    locale: 'English (United States)',
    name: 'Microsoft Server Speech Text to Speech Voice (en-US, JennyNeural)',
    sampleaudio: '',
    samplerate: '48000',
    status: 'GA',
    text: 'en-US-JennyNeural',
    type: 'Neural',
    styles: [
      'assistant',
      'chat',
      'customerservice',
      'newscast',
      'angry',
      'cheerful',
      'sad',
      'excited',
      'friendly',
      'terrified',
      'shouting',
      'unfriendly',
      'whispering',
      'hopeful',
    ],
  } as Speaker,
  speed: 'default',
  pitch: 'default',
  volume: 'default',
  style: undefined,
  pronunciation: null,
  break: null,
};

export const useStore = create<PageState>((set, get) => {
  return {
    isLoading: false,

    setIsLoading: (isLoading: boolean) => {
      set({ isLoading });
    },

    blocks: [
      {
        id: uuid(),
        html: '',
        controls: { ...INITIAL_CONTROLS },
        selections: [],
      },
    ],
    getBlock: (id: string) => {
      const blocks = get().blocks;
      const blockIndex = blocks.findIndex((block) => block.id === id);
      return blocks[blockIndex];
    },
    updateBlockContent: (id: string, content: string) => {
      const { cachedAudios, blocks } = get();

      const audioIndex = cachedAudios.findIndex(
        (audio) => audio.blockId === id
      );

      if (cachedAudios[audioIndex] && !cachedAudios[audioIndex].hasChanged) {
        const audio = {
          blockId: cachedAudios[audioIndex].blockId,
          url: cachedAudios[audioIndex].url,
          hasChanged: true,
        };

        cachedAudios.splice(audioIndex, 1, audio);

        set({ cachedAudios });
      }

      const blockIndex = blocks.findIndex((block) => block.id === id);
      blocks[blockIndex].html = content.replaceAll('&nbsp;', ' ');
      set({ blocks });
    },
    updateBlockControl: (
      id: string,
      controlName: keyof BlockControl,
      controlValue: any
    ) => {
      const { blocks, cachedAudios } = get();

      const audioIndex = cachedAudios.findIndex(
        (audio) => audio.blockId === id
      );

      if (cachedAudios[audioIndex] && !cachedAudios[audioIndex].hasChanged) {
        const audio = {
          blockId: cachedAudios[audioIndex].blockId,
          url: cachedAudios[audioIndex].url,
          hasChanged: true,
        };

        cachedAudios.splice(audioIndex, 1, audio);

        set({ cachedAudios });
      }

      const blockIndex = blocks.findIndex((block) => block.id === id);

      if (controlName === 'speaker') {
        blocks[blockIndex].controls.speaker = controlValue;
        blocks[blockIndex].controls.style = undefined;
      } else {
        blocks[blockIndex].controls[controlName] = controlValue;
      }

      set({ blocks });
    },
    clearBlockControls: (id: string) => {
      const { blocks, cachedAudios } = get();

      const audioIndex = cachedAudios.findIndex(
        (audio) => audio.blockId === id
      );

      if (cachedAudios[audioIndex] && !cachedAudios[audioIndex].hasChanged) {
        const audio = {
          blockId: cachedAudios[audioIndex].blockId,
          url: cachedAudios[audioIndex].url,
          hasChanged: true,
        };

        cachedAudios.splice(audioIndex, 1, audio);

        set({ cachedAudios });
      }

      const blockIndex = blocks.findIndex((block) => block.id === id);

      blocks[blockIndex].controls = { ...INITIAL_CONTROLS };
      blocks[blockIndex].selections = [];

      set({ blocks });
    },
    addBlock: (currentBlock: BlockElement, newBlockContent: string) => {
      const blocks = get().blocks;
      const blockIndex = blocks.findIndex(
        (block) => block.id === currentBlock.id
      );

      blocks[blockIndex].html = blocks[blockIndex].html
        .replace('&nbsp;', ' ')
        .trim();

      const newBlock: Block = {
        id: uuid(),
        html: newBlockContent,
        controls: { ...blocks[blockIndex].controls } ?? { ...INITIAL_CONTROLS },
        selections: [],
      };

      blocks.splice(blockIndex + 1, 0, newBlock);
      set({ blocks, hasAddedBlock: currentBlock });
    },
    deleteBlock: (currentBlock: BlockElement) => {
      const previousBlock =
        currentBlock.ref?.parentElement?.previousElementSibling?.querySelector(
          '.sentence-block'
        ) as HTMLElement;

      if (previousBlock) {
        const blocks = get().blocks;
        const blockIndex = blocks.findIndex(
          (block) => block.id === currentBlock.id
        );
        blocks.splice(blockIndex, 1);
        set({ blocks });

        setCaretToEnd(previousBlock);
      }
    },
    convertToBlocks: (
      content: string,
      id: string,
      cursorPosition: number,
      rangeCount: number
    ) => {
      const blocks = get().blocks;

      const sentences = content.split(/\r?\n/).filter((element) => {
        return element !== '';
      });

      const block = blocks.find((block) => block.id === id);
      const index = blocks.map((block) => block.id).indexOf(id);

      const newBlocks: Block[] = [];

      if (block) {
        block.html = block.html.replace(/&nbsp;/g, ' ');
      }

      sentences.forEach((sentence, idx) => {
        if (idx === 0 && block) {
          block.html =
            block.html.slice(0, cursorPosition - rangeCount) +
            sentence +
            block.html.slice(cursorPosition);
          newBlocks.push(block);
          return;
        }

        newBlocks.push({
          id: uuid(),
          html: sentence,
          controls: block?.controls
            ? { ...block.controls }
            : { ...INITIAL_CONTROLS },
          selections: [],
        });
      });

      blocks.splice(index, 1, ...newBlocks);

      set({ blocks });
    },

    createEffectSelection: (
      blockId: string,
      start: number,
      end: number,
      control: string,
      value: string | PronunciationEffect
    ) => {
      const { blocks, cachedAudios } = get();

      const audioIndex = cachedAudios.findIndex(
        (audio) => audio.blockId === blockId
      );

      if (cachedAudios[audioIndex] && !cachedAudios[audioIndex].hasChanged) {
        const audio = {
          blockId: cachedAudios[audioIndex].blockId,
          url: cachedAudios[audioIndex].url,
          hasChanged: true,
        };

        cachedAudios.splice(audioIndex, 1, audio);

        set({ cachedAudios });
      }

      const blockIndex = blocks.findIndex((block) => block.id === blockId);

      const alreadyHasEffect = blocks[blockIndex].selections.find(
        (selection) =>
          (start >= selection.start && start <= selection.end) ||
          (end >= selection.start && end <= selection.end) ||
          (start <= selection.start && end >= selection.end)
      );

      if (alreadyHasEffect) {
        if (start === alreadyHasEffect.start && end === alreadyHasEffect.end) {
          const updatedControls = alreadyHasEffect.controls.filter(
            (c) => c.control !== control
          );

          updatedControls.push({
            control,
            value,
          });

          alreadyHasEffect.controls = updatedControls;
        } else {
          const previousSelections = blocks[blockIndex].selections.filter(
            (selection) =>
              !(
                (start >= selection.start && start <= selection.end) ||
                (end >= selection.start && end <= selection.end) ||
                (start <= selection.start && end >= selection.end)
              )
          );

          const newSelection: EffectSelection = {
            id: uuid(),
            start,
            end,
            controls: [
              {
                control,
                value,
              },
            ],
          };

          blocks[blockIndex].selections = [...previousSelections, newSelection];
        }
      } else {
        const newSelection: EffectSelection = {
          id: uuid(),
          start,
          end,
          controls: [
            {
              control,
              value,
            },
          ],
        };

        blocks[blockIndex].selections.push(newSelection);
      }

      set({ blocks });
    },
    removeEffectFromSelection: (
      blockId: string,
      selectionId: string,
      effectName: string
    ) => {
      const { blocks, cachedAudios } = get();

      const audioIndex = cachedAudios.findIndex(
        (audio) => audio.blockId === blockId
      );

      if (cachedAudios[audioIndex] && !cachedAudios[audioIndex].hasChanged) {
        const audio = {
          blockId: cachedAudios[audioIndex].blockId,
          url: cachedAudios[audioIndex].url,
          hasChanged: true,
        };

        cachedAudios.splice(audioIndex, 1, audio);

        set({ cachedAudios });
      }

      const blockIndex = blocks.findIndex((block) => block.id === blockId);

      const selectionIndex = blocks[blockIndex].selections.findIndex(
        (selection) => selection.id === selectionId
      );

      if (selectionIndex > -1) {
        const effectIndex = blocks[blockIndex].selections[
          selectionIndex
        ].controls.findIndex((effect) => effect.control === effectName);

        if (effectIndex >= 0) {
          if (
            blocks[blockIndex].selections[selectionIndex].controls.length === 1
          ) {
            blocks[blockIndex].selections[selectionIndex].controls = [];
          } else {
            blocks[blockIndex].selections[selectionIndex].controls.splice(
              effectIndex,
              1
            );
          }
        }

        blocks[blockIndex].selections = blocks[blockIndex].selections.filter(
          (s) => s.controls.length > 0
        );

        set({ blocks });
      }
    },
    deleteEffectSelection: (blockId: string, selections: string[]) => {
      const { blocks, cachedAudios } = get();

      const audioIndex = cachedAudios.findIndex(
        (audio) => audio.blockId === blockId
      );

      if (cachedAudios[audioIndex] && !cachedAudios[audioIndex].hasChanged) {
        const audio = {
          blockId: cachedAudios[audioIndex].blockId,
          url: cachedAudios[audioIndex].url,
          hasChanged: true,
        };

        cachedAudios.splice(audioIndex, 1, audio);

        set({ cachedAudios });
      }

      const blockIndex = blocks.findIndex((block) => block.id === blockId);

      const updatedSelections = blocks[blockIndex].selections.filter(
        (selection) => !selections.includes(selection.id)
      );

      blocks[blockIndex].selections = updatedSelections;

      set({ blocks });
    },

    hasAddedBlock: undefined,
    setHasAddedBlock: (blockElement: BlockElement | undefined) => {
      set({ hasAddedBlock: blockElement });
    },

    cachedAudios: [],
    setCachedAudios: (cachedAudios: CachedAudio[]) => {
      set({ cachedAudios });
    },

    projectInfo: undefined,
    setProjectInfo: (projectInfo: ProjectInfo | undefined) => {
      set({ projectInfo });
    },
    createProject: () => {
      set({
        projectInfo: undefined,
        blocks: [
          {
            id: uuid(),
            html: '',
            controls: { ...INITIAL_CONTROLS },
            selections: [],
          },
        ],
      });
    },
    loadProject: (id: string, credentials: TeamsUserCredential | undefined) => {
      set({ isLoading: true });

      callFunction(
        true,
        'getProjectInfo?{0}&id=' + id,
        undefined,
        false,
        credentials
      )
        .then((data) => {
          if (data.data) {
            const parsedData = JSON.parse(data.data);

            const projectBlocks: Block[] = parsedData.map((block: any) => {
              return {
                id: block.id ?? crypto.randomUUID(),
                controls: block.controls ?? {},
                selections: block.selections ?? [],
                html: block.html ?? 'falhou',
              };
            });

            set({
              projectInfo: {
                id: data.id,
                description: data.description,
                name: data.name,
              },
              blocks: projectBlocks,
            });
          }
        })
        .catch(() => {
          toast.error('Project not found');
        })
        .finally(() => set({ isLoading: false }));
    },
  };
});

function setCaretToEnd(element: HTMLElement) {
  const range = document.createRange();
  const selection = window.getSelection();
  range.selectNodeContents(element);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
  element.focus();
}
